import React from 'react';
import './TemporalStackedBarChart.css';

const DEFAULT_COLORS = [
  '#0D6A3C',
  '#44A348',
  '#8DC63F',
  '#D7E931',
  '#167F4C',
  '#5DBB63',
  '#A9D646',
  '#E2F26A',
];

const DEFAULT_MARGIN = {
  top: 24,
  right: 24,
  bottom: 56,
  left: 72,
};

const TIME_KEY_TYPES = {
  DATE: 'date',
  DAY: 'day',
  WEEK: 'week',
  MONTH: 'month',
  YEAR: 'year',
};

const asNumber = (value) => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }

  if (typeof value === 'string') {
    let normalizedValue = value.trim();

    if (normalizedValue.includes(',') && normalizedValue.includes('.')) {
      normalizedValue = normalizedValue.replace(/\./g, '').replace(',', '.');
    } else if (normalizedValue.includes(',')) {
      normalizedValue = normalizedValue.replace(',', '.');
    }

    const parsedValue = Number(normalizedValue);

    return Number.isFinite(parsedValue) ? parsedValue : 0;
  }

  return 0;
};

const getCollectionValues = (group) => {
  if (Array.isArray(group)) {
    return group;
  }

  if (group && typeof group === 'object') {
    return Object.keys(group)
      .sort((firstKey, secondKey) => Number(firstKey) - Number(secondKey))
      .map((key) => group[key]);
  }

  return [];
};

const parseDateString = (dateKey) => {
  const date = new Date(`${dateKey}T00:00:00`);

  return Number.isNaN(date.getTime()) ? null : date;
};

const getShortMonthLabel = (date, locale) => {
  const monthLabel = new Intl.DateTimeFormat(locale, { month: 'short' }).format(date);
  const sanitizedLabel = monthLabel.replace('.', '');

  return sanitizedLabel.charAt(0).toUpperCase() + sanitizedLabel.slice(1);
};

const hasMonthlyCadence = (dateKeys) =>
  dateKeys.length > 0 &&
  dateKeys.every((dateKey) => {
    const parsedDate = parseDateString(dateKey);

    return parsedDate && parsedDate.getDate() === 1;
  });

const defaultDateFormatter = (dateKey, locale, isMonthlyCadence) => {
  const parsedDate = parseDateString(dateKey);

  if (!parsedDate) {
    return dateKey;
  }

  if (isMonthlyCadence) {
    return getShortMonthLabel(parsedDate, locale);
  }

  return new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: '2-digit',
  }).format(parsedDate);
};

const defaultDateFormatterWithYear = (dateKey, locale, isMonthlyCadence) => {
  const parsedDate = parseDateString(dateKey);

  if (!parsedDate) {
    return dateKey;
  }

  if (isMonthlyCadence) {
    const yearLabel = String(parsedDate.getFullYear()).slice(-2);

    return `${getShortMonthLabel(parsedDate, locale)}/${yearLabel}`;
  }

  return new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  }).format(parsedDate);
};

const defaultValueFormatter = (value, locale, currency) =>
  new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    ...(currency
      ? {
          style: 'currency',
          currency,
        }
      : {}),
  }).format(value);

const getRoundedMaxValue = (highestValue, tickCount) => {
  if (!highestValue) {
    return tickCount;
  }

  const roughStep = highestValue / tickCount;
  const magnitude = 10 ** Math.floor(Math.log10(roughStep));
  const normalizedStep = roughStep / magnitude;

  let roundedStep = magnitude;

  if (normalizedStep > 5) {
    roundedStep = 10 * magnitude;
  } else if (normalizedStep > 2) {
    roundedStep = 5 * magnitude;
  } else if (normalizedStep > 1) {
    roundedStep = 2 * magnitude;
  }

  return Math.ceil(highestValue / roundedStep) * roundedStep;
};

const isRelativeTimeKeyType = (timeKeyType) => timeKeyType !== TIME_KEY_TYPES.DATE;

const getNextRelativeTimeKey = (lastKey) => {
  const numericLastKey = Number(lastKey);

  if (Number.isFinite(numericLastKey)) {
    return String(numericLastKey + 1);
  }

  return String(lastKey);
};

const getNextDateKey = (lastKey, sortedDateKeys) => {
  const lastDate = parseDateString(lastKey);

  if (!lastDate) {
    return lastKey;
  }

  const nextDate = new Date(lastDate);

  if (hasMonthlyCadence(sortedDateKeys)) {
    nextDate.setMonth(nextDate.getMonth() + 1);
  } else {
    nextDate.setDate(nextDate.getDate() + 1);
  }

  const year = nextDate.getFullYear();
  const month = String(nextDate.getMonth() + 1).padStart(2, '0');
  const day = String(nextDate.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

const extendGroupsToTotalColumns = ({ groups, totalColumns, timeKeyType, sortedDateKeys }) => {
  const normalizedTotalColumns = Math.max(0, Math.floor(Number(totalColumns) || 0));

  if (!groups.length || !normalizedTotalColumns || groups.length >= normalizedTotalColumns) {
    return groups;
  }

  const extendedGroups = [...groups];

  while (extendedGroups.length < normalizedTotalColumns) {
    const lastGroup = extendedGroups[extendedGroups.length - 1];
    const nextDateKey = lastGroup
      ? (isRelativeTimeKeyType(timeKeyType)
          ? getNextRelativeTimeKey(lastGroup.dateKey)
          : getNextDateKey(lastGroup.dateKey, sortedDateKeys))
      : '1';

    extendedGroups.push({
      dateKey: nextDateKey,
      items: [],
      total: 0,
    });
  }

  return extendedGroups;
};

const sortTemporalKeys = (keys, timeKeyType) => {
  if (isRelativeTimeKeyType(timeKeyType)) {
    return keys.slice().sort((firstKey, secondKey) => {
      const firstValue = Number(firstKey);
      const secondValue = Number(secondKey);
      const bothNumeric = Number.isFinite(firstValue) && Number.isFinite(secondValue);

      if (bothNumeric) {
        return firstValue - secondValue;
      }

      return firstKey.localeCompare(secondKey);
    });
  }

  return keys.slice().sort((firstDate, secondDate) => firstDate.localeCompare(secondDate));
};

const buildNormalizedGroups = ({ data, valueKey, labelKey, colorKey, colors, timeKeyType }) => {
  const sortedDateKeys = sortTemporalKeys(Object.keys(data || {}), timeKeyType);
  const colorMap = {};
  const legendItems = [];
  let nextColorIndex = 0;

  const groups = sortedDateKeys.map((dateKey) => {
    const rawItems = getCollectionValues(data[dateKey]);
    const items = rawItems
      .map((item, index) => {
        const label = item?.[labelKey] || `Item ${index + 1}`;
        const explicitColor = colorKey ? item?.[colorKey] : null;

        if (!colorMap[label]) {
          colorMap[label] = explicitColor || colors[nextColorIndex % colors.length];
          legendItems.push({ label, color: colorMap[label] });
          nextColorIndex += 1;
        }

        return {
          color: explicitColor || colorMap[label],
          label,
          value: Math.max(asNumber(item?.[valueKey]), 0),
        };
      })
      .filter((item) => item.value > 0);

    return {
      dateKey,
      items,
      total: items.reduce((accumulator, item) => accumulator + item.value, 0),
    };
  });

  return {
    groups,
    legendItems,
    sortedDateKeys,
  };
};

const TemporalStackedBarChart = ({
  data = {},
  valueKey,
  labelKey,
  colorKey,
  yLabel,
  xLabel,
  colors = DEFAULT_COLORS,
  height = 360,
  tickCount = 4,
  currency = 'BRL',
  locale = 'pt-BR',
  axisLabel = 'Valor',
  timeKeyType = TIME_KEY_TYPES.DATE,
  totalColumns,
  className = '',
  style,
  maxValue,
  showLegend = true,
  showTotals = true,
  formatDateLabel,
  formatValue,
}) => {
  const { groups, legendItems, sortedDateKeys } = buildNormalizedGroups({
    data,
    valueKey,
    labelKey,
    colorKey,
    colors,
    timeKeyType,
  });
  const groupsToRender = extendGroupsToTotalColumns({
    groups,
    totalColumns,
    timeKeyType,
    sortedDateKeys,
  });

  if (!groupsToRender.length) {
    return (
      <div className={`temporal-stacked-bar-chart ${className}`.trim()} style={style}>
        <div className="temporal-stacked-bar-chart__empty">Sem {yLabel} para exibir.</div>
      </div>
    );
  }

  const margin = DEFAULT_MARGIN;
  const chartHeight = Math.max(height - margin.top - margin.bottom, 120);
  const chartWidth = 1000 - margin.left - margin.right;
  const groupCount = groupsToRender.length;
  const groupStep = chartWidth / groupCount;
  const barWidthRatio = groupCount >= 36 ? 0.22 : groupCount >= 24 ? 0.28 : groupCount >= 12 ? 0.36 : 0.48;
  const barWidth = Math.min(64, Math.max(10, groupStep * barWidthRatio));
  const totalsRotationAngle = groupCount >= 10 ? -32 : groupCount >= 7 ? -22 : 0;
  const usesRelativeTimeKeys = isRelativeTimeKeyType(timeKeyType);
  const fullSortedDateKeys = groupsToRender.map((group) => group.dateKey);
  const isMonthlyCadence = !usesRelativeTimeKeys && hasMonthlyCadence(fullSortedDateKeys);
  const valueFormatter = formatValue || ((value) => defaultValueFormatter(value, locale, currency));
  const baseDateFormatter =
    formatDateLabel ||
    ((dateKey) =>
      usesRelativeTimeKeys
        ? String(dateKey)
        : defaultDateFormatter(dateKey, locale, isMonthlyCadence));
  const computedMaxValue = Math.max(...groupsToRender.map((group) => group.total), 0);
  const yMax = Math.max(maxValue || 0, getRoundedMaxValue(computedMaxValue, tickCount));
  const ticks = Array.from({ length: tickCount + 1 }, (_, index) => (yMax / tickCount) * index);
  const baseLabels = groupsToRender.map((group) => baseDateFormatter(group.dateKey));
  const hasDuplicateBaseLabels = !usesRelativeTimeKeys && new Set(baseLabels).size !== baseLabels.length;
  const resolvedDateLabels = groupsToRender.reduce((accumulator, group, index) => {
    const baseLabel = baseLabels[index];

    accumulator[group.dateKey] =
      !formatDateLabel && hasDuplicateBaseLabels
        ? defaultDateFormatterWithYear(group.dateKey, locale, isMonthlyCadence)
        : baseLabel;

    return accumulator;
  }, {});

  return (
    <div className={`temporal-stacked-bar-chart ${className}`.trim()} style={style}>
      <svg
        className="temporal-stacked-bar-chart__svg"
        viewBox={`0 0 1000 ${height}`}
        role="img"
        aria-label="Grafico temporal de barras empilhadas"
      >
        <text
          className="temporal-stacked-bar-chart__axis-label"
          x={margin.left}
          y={16}
          textAnchor="start"
        >
          {axisLabel}
        </text>

        {ticks.map((tickValue) => {
          const y = margin.top + chartHeight - (tickValue / yMax) * chartHeight;

          return (
            <g key={tickValue}>
              <line
                className="temporal-stacked-bar-chart__grid-line"
                x1={margin.left}
                y1={y}
                x2={margin.left + chartWidth}
                y2={y}
              />
              <text
                className="temporal-stacked-bar-chart__tick-label"
                x={margin.left - 12}
                y={y + 4}
                textAnchor="end"
              >
                {valueFormatter(tickValue)}
              </text>
            </g>
          );
        })}

        <line
          className="temporal-stacked-bar-chart__axis"
          x1={margin.left}
          y1={margin.top}
          x2={margin.left}
          y2={margin.top + chartHeight}
        />
        <line
          className="temporal-stacked-bar-chart__axis"
          x1={margin.left}
          y1={margin.top + chartHeight}
          x2={margin.left + chartWidth}
          y2={margin.top + chartHeight}
        />

        {groupsToRender.map((group, groupIndex) => {
          const groupX = margin.left + groupStep * groupIndex + (groupStep - barWidth) / 2;
          let stackedHeight = 0;

          return (
            <g key={group.dateKey}>
              {group.items.map((item, itemIndex) => {
                const segmentHeight = (item.value / yMax) * chartHeight;
                const y = margin.top + chartHeight - stackedHeight - segmentHeight;
                const isTopSegment = itemIndex === group.items.length - 1;

                stackedHeight += segmentHeight;

                return (
                  <rect
                    key={`${group.dateKey}-${item.label}`}
                    className="temporal-stacked-bar-chart__segment"
                    x={groupX}
                    y={y}
                    width={barWidth}
                    height={segmentHeight}
                    rx={isTopSegment ? 6 : 0}
                    ry={isTopSegment ? 6 : 0}
                    fill={item.color}
                  >
                    <title>{`${group.dateKey} | ${item.label}: ${valueFormatter(item.value)}`}</title>
                  </rect>
                );
              })}

              {showTotals && group.items.length > 0 ? (
                <text
                  className="temporal-stacked-bar-chart__bar-total"
                  x={groupX + barWidth / 2}
                  y={margin.top + chartHeight - stackedHeight - 10}
                  textAnchor="middle"
                  transform={
                    totalsRotationAngle
                      ? `rotate(${totalsRotationAngle} ${groupX + barWidth / 2} ${margin.top + chartHeight - stackedHeight - 10})`
                      : undefined
                  }
                >
                  {valueFormatter(group.total)}
                </text>
              ) : null}

              <text
                className="temporal-stacked-bar-chart__x-label"
                x={groupX + barWidth / 2}
                y={margin.top + chartHeight + 28}
                textAnchor="middle"
              >
                {resolvedDateLabels[group.dateKey]}
              </text>
            </g>
          );
        })}
      </svg>

      {showLegend && legendItems.length ? (
        <div className="temporal-stacked-bar-chart__legend">
          {legendItems.map((item) => (
            <span className="temporal-stacked-bar-chart__legend-item" key={item.label}>
              <span
                className="temporal-stacked-bar-chart__legend-swatch"
                style={{ backgroundColor: item.color }}
              />
              {item.label}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default TemporalStackedBarChart;
